﻿import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import type { BusinessTypeKey } from "../../api/types";
import { INDUSTRY_ICONS } from "./industryIcons";

interface Props {
  businessType: BusinessTypeKey;
  /** True if this business type is the currently-applied one for this business. */
  active?: boolean;
  /** Optional label for the active badge (e.g. "Currently using"). */
  activeLabel?: string;
  /** Pre-computed Detailed field count, shown as a quick "richness" hint. */
  detailedFieldCount?: number;
  onChoose: (businessType: BusinessTypeKey) => void;
}

/**
 * Screen-1 card in the two-screen preset picker. Picks a business type only;
 * level is picked on the second screen. Click the explicit "Choose" button at
 * the bottom to advance. Shows an "active" badge when this card matches the
 * business's currently-applied preset.
 */
export function PresetCard({
  businessType,
  active,
  activeLabel,
  detailedFieldCount,
  onChoose,
}: Props) {
  const { t } = useTranslation();
  const Icon = INDUSTRY_ICONS[businessType];
  const name = t(`settings.formPresets.industry.${businessType}.name` as never) as string;
  const tagline = t(`settings.formPresets.industry.${businessType}.tagline` as never) as string;

  return (
    <article
      data-testid={`preset-card-${businessType}`}
      data-active={active ? "true" : undefined}
      className={
        "flex flex-col gap-3 rounded-xl border bg-white dark:bg-slate-800 p-4 shadow-sm transition-all " +
        (active
          ? "border-accent-500 ring-1 ring-accent-300 dark:ring-accent-700"
          : "border-slate-200 dark:border-slate-700 hover:border-accent-300 dark:hover:border-accent-600 hover:shadow-md")
      }
    >
      <header className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-semibold text-fg-primary">{name}</h4>
            {active && (
              <span
                title={activeLabel}
                data-testid={`preset-card-${businessType}-active-badge`}
                className="inline-flex items-center gap-0.5 rounded-full bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-200 px-1.5 py-0.5 text-[10px] font-medium"
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
                {activeLabel}
              </span>
            )}
          </div>
          <p className="text-xs text-fg-secondary">{tagline}</p>
        </div>
      </header>

      <div className="mt-auto flex items-end justify-between gap-2">
        {detailedFieldCount != null && (
          <span className="text-[11px] text-fg-muted">
            {t("settings.formPresets.upToFields", { count: detailedFieldCount })}
          </span>
        )}
        <button
          type="button"
          onClick={() => onChoose(businessType)}
          data-testid={`preset-card-${businessType}-choose`}
          className={
            "ml-auto rounded-lg px-3 py-1.5 text-xs font-medium transition-colors " +
            (active
              ? "bg-accent-100 text-accent-800 hover:bg-accent-200 dark:bg-accent-900/40 dark:text-accent-200 dark:hover:bg-accent-900/60"
              : "bg-accent-600 text-white hover:bg-accent-700")
          }
        >
          {active
            ? t("settings.formPresets.changeLevel")
            : t("settings.formPresets.choose")}
        </button>
      </div>
    </article>
  );
}
